
import React, { useMemo, useState, useEffect } from 'react';
import { MindMapNode, Notebook, User } from '../types';
import { Printer, Calendar, User as UserIcon, List, ChevronRight, BookOpen, Maximize2, Minimize2, Plus, Minus, MoreVertical, X, Bold, Italic, Underline, Expand, Type } from 'lucide-react';
import { parseMarkdown, reflowText } from '../utils/markdownUtils';

interface BlogViewProps {
    notebook: Notebook;
    user: User | null;
    onClose: () => void;
    onUpdateNode?: (id: string, data: Partial<MindMapNode>) => void;
    onExpandNote?: (id: string) => void;
}

interface HierarchyNode extends MindMapNode {
    childrenNodes: HierarchyNode[];
    level: number;
    chapterNumber: string;
}

interface TOCItemProps {
    node: HierarchyNode;
    activeId: string | null;
    onScrollTo: (id: string) => void;
}

const TOCItem: React.FC<TOCItemProps> = ({ node, activeId, onScrollTo }) => {
    const isActive = activeId === node.id;
    const indent = Math.min(node.level, 5) * 12 + 8;

    return (
        <div className="flex flex-col">
            <button
                id={`toc-${node.id}`}
                onClick={() => onScrollTo(node.id)}
                className={`text-left py-1.5 px-2 rounded-lg text-sm transition-all mb-0.5 flex items-start gap-2 ${
                    isActive
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-semibold'
                        : node.level === 0
                        ? 'text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-slate-800'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800'
                }`}
                style={{ paddingLeft: `${indent}px` }}
            >
                {node.chapterNumber && (
                    <span className="font-mono text-[10px] text-gray-400 mt-0.5 min-w-[24px] flex-shrink-0">{node.chapterNumber}</span>
                )}
                <span className="truncate">{node.title || 'Untitled Section'}</span>
            </button>
            {node.childrenNodes.map(child => (
                <TOCItem key={child.id} node={child} activeId={activeId} onScrollTo={onScrollTo} />
            ))}
        </div>
    );
};


export const BlogView: React.FC<BlogViewProps> = ({ notebook, user, onClose, onUpdateNode, onExpandNote }) => {
    const [activeId, setActiveId] = useState<string | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isWideView, setIsWideView] = useState(false);
    const [fontSize, setFontSize] = useState(18);
    type FontFamily = 'font-sans' | 'font-jakarta' | 'font-outfit' | 'font-space' | 'font-serif';
    const [fontFamily, setFontFamily] = useState<FontFamily>('font-sans');
    const [showMobileTools, setShowMobileTools] = useState(false);
    const [lightboxImage, setLightboxImage] = useState<string | null>(null);

    // Pagination for large notebooks
    const [currentPage, setCurrentPage] = useState(0);
    const NODES_PER_PAGE = 50;
    const pendingScrollRef = React.useRef<string | null>(null);

    // After a cross-page TOC navigation, scroll to the target section once DOM is ready
    React.useEffect(() => {
        if (!pendingScrollRef.current) return;
        const id = pendingScrollRef.current;
        pendingScrollRef.current = null;
        requestAnimationFrame(() => {
            const el = document.getElementById(`section-${id}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setActiveId(id);
                setMobileMenuOpen(false);
            }
        });
    }, [currentPage]);

    // ... (existing code) ...

    const cycleFont = () => {
        const fonts: FontFamily[] = ['font-sans', 'font-jakarta', 'font-outfit', 'font-space', 'font-serif'];
        const currentIndex = fonts.indexOf(fontFamily);
        const nextIndex = (currentIndex + 1) % fonts.length;
        setFontFamily(fonts[nextIndex]);
    };

    const getFontName = () => {
        switch (fontFamily) {
            case 'font-sans': return 'Inter';
            case 'font-jakarta': return 'Jakarta';
            case 'font-outfit': return 'Outfit';
            case 'font-space': return 'Space';
            case 'font-serif': return 'Serif';
        }
    };



    // Floating toolbar - use refs for data to avoid re-renders that clear selection
    const [toolbarVisible, setToolbarVisible] = useState(false);
    const toolbarDataRef = React.useRef<{
        position: { x: number; y: number };
        selectedText: string;
        nodeId: string | null;
    }>({ position: { x: 0, y: 0 }, selectedText: '', nodeId: null });

    const { hierarchy, flatList } = useMemo(() => {
        if (!notebook.nodes || notebook.nodes.length === 0) return { hierarchy: [], flatList: [] };

        const nodeMap = new Map<string, MindMapNode>();
        const childrenMap = new Map<string, string[]>();

        notebook.nodes.forEach(n => {
            nodeMap.set(n.id, n);
            childrenMap.set(n.id, []);
        });

        notebook.nodes.forEach(n => {
            if (n.parentId && childrenMap.has(n.parentId)) {
                childrenMap.get(n.parentId)?.push(n.id);
            }
        });

        const buildNode = (id: string, level: number, prefix: string): HierarchyNode | null => {
            const node = nodeMap.get(id);
            if (!node) return null;

            const childrenIds = childrenMap.get(id) || [];
            const childNodesRaw = childrenIds.map(cid => nodeMap.get(cid)!).filter(Boolean);
            childNodesRaw.sort((a, b) => a.position.y - b.position.y);

            const hierarchyChildren: HierarchyNode[] = [];

            childNodesRaw.forEach((child, index) => {
                let childNumber = "";
                if (level < 1 && prefix) {
                    childNumber = `${prefix}${index + 1}.`;
                }

                const hNode = buildNode(child.id, level + 1, childNumber);
                if (hNode) hierarchyChildren.push(hNode);
            });

            return {
                ...node,
                childrenNodes: hierarchyChildren,
                level,
                chapterNumber: prefix ? prefix.slice(0, -1) : ""
            };
        };

        const roots = notebook.nodes.filter(n => !n.parentId || !nodeMap.has(n.parentId));
        roots.sort((a, b) => a.position.y - b.position.y);

        const hierarchy: HierarchyNode[] = [];
        const flatList: HierarchyNode[] = [];

        roots.forEach((root, index) => {
            const rootNumber = `${index + 1}.`;
            const hNode = buildNode(root.id, 0, rootNumber);
            if (hNode) hierarchy.push(hNode);
        });

        const flatten = (nodes: HierarchyNode[]) => {
            nodes.forEach(n => {
                flatList.push(n);
                flatten(n.childrenNodes);
            });
        };
        flatten(hierarchy);

        return { hierarchy, flatList };
    }, [notebook.nodes]);

    const scrollToId = (id: string) => {
        const nodeIndex = flatList.findIndex(n => n.id === id);
        if (nodeIndex === -1) return;

        const targetPage = Math.floor(nodeIndex / NODES_PER_PAGE);
        if (targetPage !== currentPage) {
            // Section is on a different page — switch page first, scroll after re-render
            pendingScrollRef.current = id;
            setCurrentPage(targetPage);
        } else {
            const el = document.getElementById(`section-${id}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setActiveId(id);
                setMobileMenuOpen(false);
            }
        }
    };

    // Handle text selection in articles
    const handleTextSelection = () => {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed || !selection.toString().trim()) {
            setToolbarVisible(false);
            return;
        }

        const text = selection.toString().trim();
        const anchorNode = selection.anchorNode;
        if (!anchorNode) return;

        // Find the closest article element to get node ID
        const articleEl = (anchorNode.nodeType === Node.TEXT_NODE
            ? anchorNode.parentElement
            : anchorNode as Element
        )?.closest('article[data-node-id]');

        if (!articleEl) {
            setToolbarVisible(false);
            return;
        }

        const nodeId = articleEl.getAttribute('data-node-id');
        if (!nodeId) return;

        // Get selection position for toolbar
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        // Store data in ref (no re-render), then show toolbar (single re-render)
        toolbarDataRef.current = {
            position: { x: rect.left + (rect.width / 2), y: rect.top - 10 },
            selectedText: text,
            nodeId: nodeId
        };
        setToolbarVisible(true);
    };

    // Apply formatting to selected text
    const applyFormat = (type: 'bold' | 'italic' | 'underline') => {
        const { nodeId, selectedText } = toolbarDataRef.current;
        if (!nodeId || !selectedText || !onUpdateNode) {
            setToolbarVisible(false);
            return;
        }

        // Find the original node from notebook.nodes (not flatList which is derived)
        const node = notebook.nodes.find(n => n.id === nodeId);
        if (!node || !node.content) {
            setToolbarVisible(false);
            return;
        }

        // Get prefix and suffix based on format type
        let prefix: string, suffix: string;
        if (type === 'bold') {
            prefix = '**'; suffix = '**';
        } else if (type === 'italic') {
            prefix = '_'; suffix = '_';
        } else {
            prefix = '~~'; suffix = '~~';
        }

        const escapedText = selectedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedText.split(/\s+/).join('\\s+'), 'g');

        // Replace first occurrence only
        let replaced = false;
        const newContent = node.content.replace(regex, (match) => {
            if (replaced) return match;
            replaced = true;
            return `${prefix}${match}${suffix}`;
        });

        if (replaced) {
            onUpdateNode(nodeId, { content: newContent });
        }

        setToolbarVisible(false);
        window.getSelection()?.removeAllRanges();
    };

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!toolbarVisible || !toolbarDataRef.current.selectedText) return;

            if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
                e.preventDefault();
                applyFormat('bold');
            } else if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
                e.preventDefault();
                applyFormat('italic');
            } else if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
                e.preventDefault();
                applyFormat('underline');
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [toolbarVisible]);

    const handlePrint = () => {
        window.print();
    };

    // Auto-scroll TOC to keep active item visible
    useEffect(() => {
        if (!activeId) return;
        const el = document.getElementById(`toc-${activeId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [activeId]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id.replace('section-', ''));
                    }
                });
            },
            { rootMargin: '-10% 0px -80% 0px' }
        );
        flatList.forEach((node) => {
            const el = document.getElementById(`section-${node.id}`);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [flatList]);

    return (
        <div className="flex h-[100dvh] w-full bg-white dark:bg-slate-950 relative overflow-hidden print:overflow-visible print:h-auto print:block z-[100]">

            {/* Floating Format Toolbar */}
            {toolbarVisible && onUpdateNode && (
                <div
                    className="fixed z-[200] bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 p-1 flex items-center gap-1 animate-in fade-in zoom-in-95 duration-150"
                    style={{
                        left: toolbarDataRef.current.position.x,
                        top: toolbarDataRef.current.position.y,
                        transform: 'translate(-50%, -100%)'
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <button
                        onClick={() => applyFormat('bold')}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors text-gray-700 dark:text-gray-200"
                        title="Bold (Ctrl+B)"
                    >
                        <Bold size={18} />
                    </button>
                    <button
                        onClick={() => applyFormat('italic')}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors text-gray-700 dark:text-gray-200"
                        title="Italic (Ctrl+I)"
                    >
                        <Italic size={18} />
                    </button>
                    <button
                        onClick={() => applyFormat('underline')}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors text-gray-700 dark:text-gray-200"
                        title="Underline (Ctrl+U)"
                    >
                        <Underline size={18} />
                    </button>
                </div>
            )}

            <aside className={`hidden md:flex flex-col border-r dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900/50 no-print print:hidden flex-shrink-0 transition-all duration-300 ease-in-out ${sidebarOpen ? 'w-80' : 'w-16 items-center'}`}>
                <div className={`border-b dark:border-slate-800 flex flex-col ${sidebarOpen ? 'p-6 items-start' : 'p-4 w-full items-center gap-4'}`}>
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className={`flex items-center gap-2 text-gray-500 uppercase tracking-widest text-xs font-bold hover:bg-gray-200 dark:hover:bg-slate-800 rounded-lg transition-colors ${sidebarOpen ? 'mb-1 -ml-1.5 p-1.5' : 'p-2'}`}
                        title={sidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
                    >
                        <List size={sidebarOpen ? 14 : 20} />
                        {sidebarOpen && <span>Table of Contents</span>}
                    </button>

                    {sidebarOpen && (
                        <h2 className="font-sans font-bold text-gray-800 dark:text-gray-200 truncate w-full" title={notebook.title}>
                            {notebook.title}
                        </h2>
                    )}
                </div>

                {sidebarOpen && (
                    <>
                        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar w-full">
                            {hierarchy.map(root => (
                                <TOCItem key={root.id} node={root} activeId={activeId} onScrollTo={scrollToId} />
                            ))}
                        </div>
                        <div className="p-4 border-t dark:border-slate-800 text-xs text-center text-gray-400 w-full">
                            {flatList.length} sections
                        </div>
                    </>
                )}
            </aside>

            <div className="md:hidden absolute top-0 left-0 right-0 h-14 bg-white dark:bg-slate-900 border-b dark:border-slate-800 z-40 flex items-center justify-between px-4 no-print print:hidden">
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-200">
                    <List size={18} /> {mobileMenuOpen ? 'Hide Contents' : 'Contents'}
                </button>
                <h1 className="text-sm font-bold truncate max-w-[150px]">{notebook.title}</h1>
            </div>

            {mobileMenuOpen && (
                <div className="md:hidden absolute top-14 left-0 w-full bg-white dark:bg-slate-900 border-b shadow-xl z-30 max-h-[60vh] overflow-y-auto p-4 no-print">
                    {hierarchy.map(root => (
                        <TOCItem key={root.id} node={root} activeId={activeId} onScrollTo={scrollToId} />
                    ))}
                </div>
            )}


            {/* Desktop: show all buttons. Mobile: show toggle button, expand on click - MOVED OUTSIDE MAIN TO PREVENT SCROLLING */}
            <div className="absolute top-24 right-6 flex flex-col gap-2 no-print print:hidden z-[150]">
                {/* Mobile toggle button */}
                <button
                    onClick={() => setShowMobileTools(!showMobileTools)}
                    className="md:hidden p-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 rounded-full shadow-xl hover:bg-blue-50 transition-all border border-gray-200 dark:border-slate-700 cursor-pointer"
                    title="Toggle Tools"
                >
                    {showMobileTools ? <X size={20} /> : <MoreVertical size={20} />}
                </button>

                {/* Tools - always visible on desktop, toggled on mobile */}
                <div className={`flex flex-col gap-2 ${showMobileTools ? 'flex' : 'hidden'} md:flex`}>
                    <button onClick={handlePrint} className="p-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 rounded-full shadow-xl hover:bg-blue-50 transition-all border border-gray-200 dark:border-slate-700 cursor-pointer" title="Print to PDF">
                        <Printer size={20} />
                    </button>
                    <button onClick={() => setIsWideView(!isWideView)} className="p-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 rounded-full shadow-xl hover:bg-blue-50 transition-all border border-gray-200 dark:border-slate-700 cursor-pointer" title={isWideView ? "Standard View" : "Wide View"}>
                        {isWideView ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                    </button>
                    <button onClick={cycleFont} className="p-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 rounded-full shadow-xl hover:bg-blue-50 transition-all border border-gray-200 dark:border-slate-700 cursor-pointer flex items-center justify-center" title={`Change Font (${getFontName()})`}>
                        <Type size={16} />
                    </button>
                    <button onClick={() => setFontSize(prev => Math.min(prev + 2, 32))} className="p-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 rounded-full shadow-xl hover:bg-blue-50 transition-all border border-gray-200 dark:border-slate-700 cursor-pointer" title="Increase Font Size">
                        <Plus size={20} />
                    </button>
                    <button onClick={() => setFontSize(prev => Math.max(prev - 2, 12))} className="p-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 rounded-full shadow-xl hover:bg-blue-50 transition-all border border-gray-200 dark:border-slate-700 cursor-pointer" title="Decrease Font Size">
                        <Minus size={20} />
                    </button>
                    <button onClick={onClose} className="p-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 rounded-full shadow-xl hover:bg-red-50 transition-all border border-gray-200 dark:border-slate-700 mt-2 cursor-pointer" title="Close Blog View">
                        <ChevronRight size={20} />
                    </button>
                </div>
            </div>

            <main className="flex-1 overflow-y-auto h-full scroll-smooth relative print:overflow-visible print:h-auto print:block" id="main-scroll-container">

                <div id="blog-printable-area" className={`mx-auto min-h-screen px-8 py-12 md:px-16 md:py-16 print:p-0 print:min-h-0 print:h-auto transition-all duration-500 ${isWideView ? 'max-w-[96%]' : 'max-w-4xl'}`}>
                    <header className="mb-20 text-center border-b-2 border-gray-100 dark:border-slate-800 pb-16 pt-8">
                        <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
                            <BookOpen size={32} />
                        </div>
                        <h1 className={`text-5xl md:text-6xl ${fontFamily} font-black text-gray-900 dark:text-white mb-8 leading-tight`}>
                            {notebook.title}
                        </h1>
                        <div className="flex items-center justify-center gap-8 text-sm text-gray-500 dark:text-gray-400 font-medium tracking-wide uppercase">
                            <div className="flex items-center gap-2">
                                <UserIcon size={14} />
                                <span>{user?.name || 'Author'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar size={14} />
                                <span>{new Date(notebook.updatedAt).toLocaleDateString()}</span>
                            </div>
                        </div>
                    </header>

                    <div className="space-y-16">
                        {/* Pagination: render only current page */}
                        {flatList.slice(currentPage * NODES_PER_PAGE, (currentPage + 1) * NODES_PER_PAGE).map((node) => {
                            const isChapter = node.level === 0;
                            const visuals = [];
                            if (node.imageBase64) visuals.push({ src: node.imageBase64, caption: "Source Material" });
                            if (node.drawingBase64) visuals.push({ src: node.drawingBase64, caption: "Sketch" });
                            if (node.attachments) {
                                node.attachments.forEach((att, idx) => {
                                    visuals.push({ src: att.content, caption: `Attachment ${idx + 1}` });
                                });
                            }

                            return (
                                <article
                                    key={node.id}
                                    id={`section-${node.id}`}
                                    data-node-id={node.id}
                                    onMouseUp={handleTextSelection}
                                    className={`print-break-inside-avoid scroll-mt-24 transition-colors duration-500 ${activeId === node.id ? 'bg-blue-50/30 dark:bg-blue-900/10 -mx-4 px-4 rounded-xl' : ''}`}
                                >
                                    <header className="mb-6 group">
                                        <div className="flex items-baseline gap-4 mb-2">
                                            {node.chapterNumber && (
                                                <span className={`font-mono font-bold text-gray-300 dark:text-gray-600 select-none ${isChapter ? 'text-4xl opacity-40' : 'text-xl opacity-60'}`}>
                                                    {node.chapterNumber}
                                                </span>
                                            )}
                                            {node.title && (
                                                isChapter ? (
                                                    <h2 className={`text-4xl ${fontFamily} font-bold text-gray-900 dark:text-white`}>{node.title}</h2>
                                                ) : (
                                                    <h3 className={`${fontFamily} font-bold text-gray-800 dark:text-gray-100 ${node.level === 1 ? 'text-2xl' : 'text-xl'}`}>{node.title}</h3>
                                                )
                                            )}
                                            {/* Expand button - visible on hover */}
                                            {onExpandNote && (
                                                <button
                                                    onClick={() => onExpandNote(node.id)}
                                                    className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all ml-2 no-print"
                                                    title="Edit in full screen"
                                                >
                                                    <Expand size={16} />
                                                </button>
                                            )}
                                        </div>
                                        {isChapter && <div className="h-1 w-20 bg-blue-500 rounded-full mt-4"></div>}
                                    </header>

                                    {visuals.length > 0 && (
                                        <div className={`my-8 grid gap-4 ${visuals.length > 1 ? 'grid-cols-2' : (isChapter ? 'float-right ml-8 w-1/2 mb-4' : 'w-full')}`}>
                                            {visuals.map((vis, idx) => (
                                                <figure key={idx} className="w-full">
                                                    <img
                                                        src={vis.src}
                                                        alt="Visual"
                                                        className="rounded-xl shadow-lg border border-gray-100 dark:border-slate-700 w-full object-cover cursor-pointer hover:opacity-95 transition-opacity"
                                                        onClick={() => setLightboxImage(vis.src)}
                                                    />
                                                    <figcaption className="text-right text-xs text-gray-400 mt-2 italic">
                                                        {vis.caption}
                                                    </figcaption>
                                                </figure>
                                            ))}
                                        </div>
                                    )}

                                    {node.content ? (
                                        <div
                                            className={`prose dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 ${fontFamily} leading-loose whitespace-pre-wrap ${isChapter ? 'text-lg' : 'text-base'}`}
                                            style={{ fontSize: fontSize + 'px' }}
                                            dangerouslySetInnerHTML={{ __html: parseMarkdown(reflowText(node.content)) }}
                                        />
                                    ) : (
                                        visuals.length === 0 && (
                                            <p className="text-gray-300 italic text-sm">...</p>
                                        )
                                    )}

                                    {node.analysis && node.analysis.score < 100 && node.content.length > 20 && (
                                        <div className="mt-6 p-5 bg-yellow-50 dark:bg-yellow-900/10 rounded-xl border-l-4 border-yellow-400 text-sm text-gray-700 dark:text-gray-400 font-sans leading-relaxed no-print print:hidden">
                                            <strong className="block text-yellow-700 dark:text-yellow-500 text-xs uppercase tracking-wider mb-1">AI Tutor Note</strong>
                                            {node.analysis.feedback}
                                        </div>
                                    )}

                                    {isChapter && <div className="clear-both pt-12 border-b dark:border-slate-800" />}
                                </article>
                            );
                        })}
                    </div>

                    {/* Pagination Controls */}
                    {flatList.length > NODES_PER_PAGE && (
                        <div className="mt-16 flex items-center justify-center gap-4 pb-8">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                                disabled={currentPage === 0}
                                className="px-6 py-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 rounded-lg shadow-md hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all border border-gray-200 dark:border-slate-700 font-medium"
                            >
                                ← Previous
                            </button>
                            <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                                Page {currentPage + 1} of {Math.ceil(flatList.length / NODES_PER_PAGE)}
                                <span className="text-gray-400 dark:text-gray-500 ml-2">
                                    (Showing {currentPage * NODES_PER_PAGE + 1}-{Math.min((currentPage + 1) * NODES_PER_PAGE, flatList.length)} of {flatList.length} sections)
                                </span>
                            </div>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(flatList.length / NODES_PER_PAGE) - 1, prev + 1))}
                                disabled={currentPage >= Math.ceil(flatList.length / NODES_PER_PAGE) - 1}
                                className="px-6 py-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 rounded-lg shadow-md hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all border border-gray-200 dark:border-slate-700 font-medium"
                            >
                                Next →
                            </button>
                        </div>
                    )}

                    <footer className={`mt-32 pt-12 border-t dark:border-slate-800 text-center text-gray-400 text-sm ${fontFamily} mb-12`}>
                        <p>© {new Date().getFullYear()} {user?.name}. Created with Study Board.</p>
                    </footer>
                </div>
            </main >

            {/* Image Lightbox */}
            {lightboxImage && (
                <div
                    className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 animate-in fade-in duration-200 backdrop-blur-sm"
                    onClick={() => setLightboxImage(null)}
                >
                    <button
                        className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10"
                        onClick={() => setLightboxImage(null)}
                    >
                        <X size={32} />
                    </button>
                    <img
                        src={lightboxImage}
                        alt="Full size visual"
                        className="max-w-full max-h-[90vh] rounded-lg shadow-2xl object-contain animate-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div >
    );
};
